import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Pagination, { usePagination } from '../components/Pagination';
import ProductForm from '../components/products/ProductForm';
import ProductRow from '../components/products/ProductRow';
import { GridIcon, SearchIcon, SparkleIcon } from '../components/icons';
import { useAuth } from '../context/AuthContext';
import { addVendor, listVendors } from '../lib/vendors';
import { listReorderItems } from '../lib/reorder';
import { resolvePreviews } from '../lib/linkPreview';
import { downloadCsv } from '../lib/csv';
import { formatJMD } from '../lib/finances';
import { todayISO } from '../lib/dates';
import { toHttpUrl } from '../lib/url';
import {
  addProduct,
  addProductToReorder,
  countByState,
  deleteProduct,
  filterProducts,
  inventoryValue,
  listProducts,
  nextStock,
  PRODUCT_CATEGORIES,
  productExportRows,
  setStock,
  sortProducts,
  updateProduct,
} from '../lib/products';
import './Products.css';
import { useConfirm } from '../components/ConfirmDialog';

const STOCK_FILTERS = ['All', 'Needs restock', 'Out of stock'];

const SORT_OPTIONS = [
  { id: 'name', label: 'Name (A–Z)' },
  { id: 'category', label: 'Category' },
  { id: 'supplier', label: 'Supplier' },
  { id: 'stock', label: 'Least stock first' },
  { id: 'price', label: 'Highest price first' },
];

// The product catalogue and its stock counts — what the product spreadsheet
// was for. One flat list rather than tabs: there's only one thing here, and
// the filters do the narrowing that separate tabs would have done.
export default function Products() {
  const confirm = useConfirm();
  const { user } = useAuth();

  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [reorderItems, setReorderItems] = useState([]);
  const [previews, setPreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [stockFilter, setStockFilter] = useState('All');
  const [sort, setSort] = useState('name');
  const [showArchived, setShowArchived] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // The reorder list only powers the "already on the order list" note, so
      // its failure is contained the same way Suppliers & Orders contains it:
      // an un-run migration must not blank the catalogue.
      const reorderPromise = listReorderItems().then(
        (rows) => setReorderItems(rows),
        () => setReorderItems([])
      );

      const [productRows, vendorRows] = await Promise.all([listProducts(), listVendors()]);
      await reorderPromise;
      setProducts(productRows);
      setVendors(vendorRows);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Thumbnails for the website links. Cached rows come back in one query; only
  // links never seen before cost a fetch through the Edge Function.
  useEffect(() => {
    const urls = products.map((p) => p.product_url).filter(Boolean);
    if (urls.length === 0) return;
    let live = true;
    resolvePreviews(urls, {
      onResolved: (next) => {
        if (live) setPreviews(next);
      },
    }).catch(() => {});
    return () => {
      live = false;
    };
  }, [products]);

  // ---- mutations --------------------------------------------------------

  const handleSubmit = async (fields) => {
    if (editing) await updateProduct(editing.id, fields);
    else await addProduct({ ...fields, created_by: user.id });
    setFormOpen(false);
    setEditing(null);
    await loadAll();
  };

  const handleCreateVendorInline = async (name) => {
    const vendor = await addVendor(name);
    setVendors((prev) => [...prev, vendor].sort((a, b) => a.name.localeCompare(b.name)));
    return vendor;
  };

  // Stock changes are applied on screen first and written after.
  //
  // Counting a shelf is a burst of clicks, and waiting for a round trip
  // between each one makes the buttons feel broken — so the number moves
  // immediately and rolls back, loudly, only if the write actually fails.
  const writeStock = async (product, quantity) => {
    const previous = product.stock_qty;
    setProducts((prev) =>
      prev.map((p) => (p.id === product.id ? { ...p, stock_qty: quantity } : p))
    );
    setActionError(null);
    try {
      await setStock(product.id, quantity);
    } catch (err) {
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, stock_qty: previous } : p))
      );
      setActionError(`Couldn't save the new count for ${product.name}: ${err.message}`);
    }
  };

  const handleAdjustStock = (product, delta) =>
    writeStock(product, nextStock(product.stock_qty, delta));

  const handleArchive = async (product) => {
    try {
      await updateProduct(product.id, { is_active: !product.is_active });
      await loadAll();
    } catch (err) {
      setActionError(`Couldn't update ${product.name}: ${err.message}`);
    }
  };

  const handleDelete = async (product) => {
    try {
      if (!(await confirm({
        title: 'Delete this product?',
        body: "Past invoices and income still reference it. Archiving keeps the history and hides it from the shelf.",
      }))) return;

      await deleteProduct(product.id);
      await loadAll();
    } catch (err) {
      setActionError(`Couldn't delete ${product.name}: ${err.message}`);
    }
  };

  const handleAddToOrder = async (product) => {
    try {
      await addProductToReorder(product, user.id);
      const rows = await listReorderItems();
      setReorderItems(rows);
      setActionError(null);
    } catch (err) {
      setActionError(`Couldn't add ${product.name} to the order list: ${err.message}`);
    }
  };

  // ---- derived ----------------------------------------------------------

  // An item counts as "already raised" only while it's still live. A received
  // or dismissed item is finished, and the product running low again is a
  // genuinely new shortage.
  const openReorderByProduct = useMemo(() => {
    const map = {};
    for (const item of reorderItems) {
      if (!item.product_id) continue;
      if (item.status === 'Needed' || item.status === 'Ordered') map[item.product_id] = item.status;
    }
    return map;
  }, [reorderItems]);

  const inScope = useMemo(
    () => products.filter((p) => (showArchived ? true : p.is_active !== false)),
    [products, showArchived]
  );

  const filtered = useMemo(
    () => sortProducts(filterProducts(inScope, { query, category, stock: stockFilter }), sort),
    [inScope, query, category, stockFilter, sort]
  );

  const { visible, controls } = usePagination(filtered);

  const counts = useMemo(() => countByState(inScope), [inScope]);
  const value = useMemo(() => inventoryValue(inScope), [inScope]);
  const restockCount = counts.out + counts.low;
  const archivedCount = products.filter((p) => p.is_active === false).length;

  const handleExport = () => {
    downloadCsv(`skinfairyja-products-${todayISO()}.csv`, productExportRows(filtered));
  };

  return (
    <>
      <PageHeader
        title={
          <>
            Products
            <SparkleIcon className="page-title-sparkle" />
          </>
        }
        subtitle="Everything you sell, what's left on the shelf, and where it lives on the site."
        actions={
          !formOpen && (
            <button
              className="primary-action"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              + Add product
            </button>
          )
        }
      />

      <div className="product-stats">
        <div className="product-stat">
          <span className="product-stat-label">Products</span>
          <span className="product-stat-value">{inScope.length}</span>
          <span className="product-stat-note">
            {counts.ok} in stock
            {archivedCount > 0 && !showArchived && ` · ${archivedCount} archived`}
          </span>
        </div>

        {/* A count you can act on, not a decorative figure: clicking it is
            the same as choosing the "Needs restock" filter. */}
        <button
          type="button"
          className={'product-stat product-stat-action' + (restockCount > 0 ? ' alert' : '')}
          onClick={() => setStockFilter(restockCount > 0 ? 'Needs restock' : 'All')}
        >
          <span className="product-stat-label">Needs restock</span>
          <span className="product-stat-value">{restockCount}</span>
          <span className="product-stat-note">
            {restockCount === 0
              ? 'Nothing running low'
              : `${counts.out} out · ${counts.low} low`}
          </span>
        </button>

        {/* Retail value leads; cost and profit are the supporting line. The
            question "what do we make on this?" is worth answering here, but
            this page is for running the shelf, not for reading margins. */}
        <div className="product-stat">
          <span className="product-stat-label">Stock value</span>
          <span className="product-stat-value">{formatJMD(value.total)}</span>
          <span className="product-stat-note">
            {value.marginKnownCount > 0
              ? `${formatJMD(value.cost)} at cost · ${formatJMD(value.potentialProfit)} profit if it all sells`
              : 'Retail value of what’s on hand'}
          </span>
          {value.unpriced > 0 && (
            <span className="product-stat-note product-stat-caveat">
              {value.unpriced} product{value.unpriced === 1 ? '' : 's'} with no price set
            </span>
          )}
        </div>
      </div>

      {loadError && (
        <p className="product-error">
          Couldn&rsquo;t load the catalogue: {loadError}. Run migration 0026 if you haven&rsquo;t
          yet.
        </p>
      )}
      {actionError && <p className="product-error">{actionError}</p>}

      {formOpen && (
        <ProductForm
          initialProduct={editing}
          products={products}
          vendors={vendors}
          onCreateVendor={handleCreateVendorInline}
          onSubmit={handleSubmit}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}

      <section className="panel">
        <div className="library-toolbar">
          <div className="library-search">
            <SearchIcon />
            <input
              type="search"
              placeholder="Search products, suppliers or descriptions…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select
            className="library-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Category"
          >
            <option value="All">All categories</option>
            {PRODUCT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            className="library-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Sort by"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>

          <button className="ghost-button" onClick={handleExport} disabled={filtered.length === 0}>
            Export CSV
          </button>
        </div>

        <div className="library-pills">
          {STOCK_FILTERS.map((s) => (
            <button
              key={s}
              className={'library-pill' + (stockFilter === s ? ' active' : '')}
              onClick={() => setStockFilter(s)}
            >
              {s}
              {s === 'Needs restock' && restockCount > 0 && (
                <span className="library-pill-count">{restockCount}</span>
              )}
            </button>
          ))}
          {archivedCount > 0 && (
            <button
              className={'library-pill' + (showArchived ? ' active' : '')}
              onClick={() => setShowArchived((v) => !v)}
            >
              Show archived
              <span className="library-pill-count">{archivedCount}</span>
            </button>
          )}
        </div>

        {loading ? (
          <p className="product-loading">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="product-empty">
            {products.length === 0 ? (
              <>
                <GridIcon />
                <p className="product-empty-lead">No products yet.</p>
                <p>
                  Add the first one and it&rsquo;ll show up here with its stock count, price and a
                  link through to the website.
                </p>
                <button
                  className="primary-action"
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  + Add product
                </button>
              </>
            ) : (
              <p className="product-empty-lead">No products match those filters.</p>
            )}
          </div>
        ) : (
          <ul className="product-list">
            {visible.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                preview={previews[toHttpUrl(product.product_url)]}
                reorderStatus={openReorderByProduct[product.id]}
                onAdjustStock={handleAdjustStock}
                onSetStock={writeStock}
                onEdit={(p) => {
                  setEditing(p);
                  setFormOpen(true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onArchive={handleArchive}
                onDelete={handleDelete}
                onAddToOrder={handleAddToOrder}
              />
            ))}
          </ul>
        )}

        <Pagination {...controls} />
      </section>
    </>
  );
}
